import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import KenLoader from '../../../../../../GlobalComponents/KenLoader';
import { getUserDetails } from '../../../../../../storageService';
import { fetchAssessmentResponseDetail, ResponseDetail } from '../../../Helper/AssessmentFormsHelper';

const useStyles = makeStyles((theme: any) => ({
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: 500,
        color: theme.palette.KenColors.CttPrimary ?? '#2563EB',
        cursor: 'pointer',
        marginBottom: '16px',
    },
    card: {
        background: '#fff',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '16px',
    },
    avatar: {
        width: '44px',
        height: '44px',
        borderRadius: '50%',
        background: theme.palette.KenColors.CttSecondary ?? '#F5F7FB',
        color: theme.palette.KenColors.CttMutedForeground ?? '#8A94A4',
        display: 'grid',
        placeItems: 'center',
        fontSize: '14px',
        fontWeight: 700,
        flexShrink: 0,
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        background: theme.palette.KenColors.CttChipSky ?? '#E9F2FB',
        color: theme.palette.KenColors.CttChipSkyForeground ?? '#2380C4',
    },
    typePill: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 12px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 500,
        background: theme.palette.KenColors.CttSecondary ?? '#F5F7FB',
        color: theme.palette.KenColors.CttMutedForeground ?? '#8A94A4',
        flexShrink: 0,
    },
    answerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '18px 0',
        borderTop: `1px solid ${theme.palette.KenColors.CttBorder ?? '#ECEFF5'}`,
        '&:first-of-type': {
            borderTop: 'none',
            paddingTop: 0,
        },
    },
    hint: {
        color: theme.palette.KenColors.CttMutedForeground ?? '#8A94A4',
        fontSize: '14px',
        textAlign: 'center',
        padding: '24px 0',
    },
}));

const initials = (name: string) =>
    name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('') || '?';

const AssessmentResponse: React.FC = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const { responseId } = useParams();
    const [loading, setLoading] = useState(true);
    const [response, setResponse] = useState<ResponseDetail | null>(null);

    useEffect(() => {
        const load = async () => {
            if (!responseId) {
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const data = await fetchAssessmentResponseDetail(responseId, 'Faculty');
                setResponse(data);
            } catch (error) {
                console.error('Error fetching response detail:', error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [responseId]);

    if (loading) return <KenLoader />;

    return (
        <Box>
            <Typography className={classes.backLink} onClick={() => navigate(-1)}>
                <ArrowBackIcon fontSize="small" /> Back to responses
            </Typography>

            <Box className={classes.card} sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                <Box className={classes.avatar}>{initials(response?.respondentName || '')}</Box>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>{response?.respondentName || 'Unknown student'}</Typography>
                    <Typography sx={{ fontSize: '13px', color: '#8A94A4', marginTop: '2px' }}>
                        {[response?.formType, response?.formTitle, response?.batchName].filter(Boolean).join(' · ')}
                    </Typography>
                </Box>
                {response?.formType && <span className={classes.pill}>{response.formType}</span>}
            </Box>

            <Box className={classes.card}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 600 }}>Answers</Typography>
                    <Typography sx={{ fontSize: '13px', color: '#8A94A4' }}>
                        {response?.answers.length ?? 0} question{(response?.answers.length ?? 0) === 1 ? '' : 's'}
                    </Typography>
                </Box>

                {!response || response.answers.length === 0 ? (
                    <Typography className={classes.hint}>No answers found for this response.</Typography>
                ) : (
                    response.answers.map((answer, index) => (
                        <Box key={index} className={classes.answerRow}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '14px', color: '#8A94A4' }}>
                                    {index + 1}. {answer.questionText}
                                </Typography>
                                <Typography sx={{ fontSize: '15px', fontWeight: 600, marginTop: '6px' }}>
                                    Answer: {answer.answerText || '—'}
                                </Typography>
                            </Box>
                            <span className={classes.typePill}>{answer.answerType === 'Open Text' ? 'Open text' : 'Choice'}</span>
                        </Box>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default AssessmentResponse;
